import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'
import { useOnError } from '@services/utils/onError'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { DepositAmendmentBody, RentAmendmentBody } from '../types'

// Errors come back as a JSON blob because of responseType: 'blob'.
async function ensurePdf(blob: Blob) {
  if (blob.type === 'application/json') {
    throw JSON.parse(await blob.text())
  }
  return blob
}

function useOpenPdf() {
  const toast = useToast()
  const { t } = useTranslation('toast')
  return (blob: Blob) => {
    const url = URL.createObjectURL(
      new Blob([blob], { type: 'application/pdf' })
    )
    if (!window.open(url, '_blank')) {
      toast.error({ message: t('popupBlocked') })
    }
    // Give the new tab time to load the blob before it is released.
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}

/** Renders the amendment PDF from the form values without sending it. */
export function useRentAmendmentPreview() {
  const onError = useOnError()
  const openPdf = useOpenPdf()

  async function preview(body: RentAmendmentBody) {
    const res = await apiClient.post<Blob>(
      `${SNA_PREFIX}rent-amendments/preview`,
      body,
      { responseType: 'blob' }
    )
    return ensurePdf(res.data)
  }

  return useMutation({ mutationFn: preview, onSuccess: openPdf, onError })
}

/** Renders the deposit amendment PDF without sending it. */
export function useDepositAmendmentPreview() {
  const onError = useOnError()
  const openPdf = useOpenPdf()

  async function preview(body: DepositAmendmentBody) {
    const res = await apiClient.post<Blob>(
      `${SNA_PREFIX}rent-amendments/deposit-preview`,
      body,
      { responseType: 'blob' }
    )
    return ensurePdf(res.data)
  }

  return useMutation({ mutationFn: preview, onSuccess: openPdf, onError })
}

/** The sent amendment — signed once everyone has signed. */
export function useRentAmendmentDocument() {
  const onError = useOnError()
  const openPdf = useOpenPdf()

  async function download(id: string) {
    const res = await apiClient.get<Blob>(
      `${SNA_PREFIX}rent-amendments/${id}/document`,
      { responseType: 'blob' }
    )
    return ensurePdf(res.data)
  }

  return useMutation({ mutationFn: download, onSuccess: openPdf, onError })
}
